import React, { memo, useCallback } from "react";
import {
  IMAGE_TEMPLATES_INFO,
  VIDEO_TEMPLATES_INFO
} from "@talentbait/remotion-templates-talentbait";

import TemplatePostActions from "../../TemplatePostActions";
import {
  MainWrapper,
  SingleTemplate,
  SingleTemplatePreview,
  TemplateLabel,
  SingleTemplatePreviewWrapper,
  ScrollButton,
  MainWrapperContainer
} from "./TemplateSelector.styled";
import { PreviewStore, withRefluxStore } from "../../../../../../Stores";
import { isCampaignUsingSimpleTemplates } from "../../TemplatePost__Preview/utils";
import { BlueLeftArrow } from "../../../../../../Components/Icons/BlueLeftArrow";
import { BlueRightArrow } from "../../../../../../Components/Icons/BlueRightArrow";
import { useScrollContainer } from "../../../../../../hooks/useScrollContainer";

/** TODO: Dec 2024
 * remove the following const when simple templates are not used in campaigns any more
 */
const SIMPLE_TEMPLATES = ["PortraitImage", "VerticalImage", "LandscapeImage"];

// TODO: Remove ShapeImage when it is not used in any campaigns any more
const tempRemovedTemplates = [
  "VerticalVideo",
  "LandscapeVideo",
  "PortraitVideo",
  "ShapeImage",
  "WaveVideo",
  "OneToneVideo"
];

let TEMPLATES = { ...IMAGE_TEMPLATES_INFO, ...VIDEO_TEMPLATES_INFO };

TEMPLATES = Object.values(TEMPLATES).filter(
  (template) => !tempRemovedTemplates.includes(template.id)
);

const orientationOrder = ["portrait", "vertical", "landscape"];

const TemplateSelector = memo(
  ({
    templateId,
    showVerticalImage = false,
    showTikTokVideoTemplate = false,
    templateTypeSelected,
    // Store
    campaignData
  }) => {
    const { scrollContainer, showButtons, scrollLeft, scrollRight } = useScrollContainer();

    const updateTemplateId = useCallback(
      (newTemplateId) => {
        TemplatePostActions.updateTemplateId(newTemplateId, templateTypeSelected);
      },
      [templateTypeSelected]
    );

    let filteredTemplates = Object.values(TEMPLATES)
      .filter((template) => template.type === templateTypeSelected)
      .sort((a, b) => {
        // Handle "multi" orientation to always be last
        if (a.orientation === "multi" && b.orientation !== "multi") return 1;
        if (b.orientation === "multi" && a.orientation !== "multi") return -1;

        // Sort non-"multi" orientations based on orientationOrder and then by IDs
        const aIndex = orientationOrder.indexOf(a.orientation);
        const bIndex = orientationOrder.indexOf(b.orientation);

        // Sort based on the orientationOrder array
        if (aIndex !== bIndex) return aIndex - bIndex;

        // If orientations are the same, sort by IDs alphabetically
        return a.id.localeCompare(b.id);
      });

    // Remove the vertical image template if it should not be shown
    if (!showVerticalImage) {
      filteredTemplates = filteredTemplates.filter((template) => template.id !== "VerticalImage");
    }

    // Remove the TikTok video template if it should not be shown
    if (!showTikTokVideoTemplate) {
      filteredTemplates = filteredTemplates.filter((template) => template.id !== "TikTokVideo");
    }

    // Remove the Upload video template if it's TikTok video
    if (showTikTokVideoTemplate) {
      filteredTemplates = filteredTemplates.filter((template) => template.id !== "RawVideo");
    }

    if (isCampaignUsingSimpleTemplates(campaignData?.status, campaignData?.createdDate)) {
      filteredTemplates = filteredTemplates.filter((template) => template.id !== "DefaultImage");
    } else {
      filteredTemplates = filteredTemplates.filter(
        (template) => !SIMPLE_TEMPLATES.includes(template.id)
      );
    }

    return (
      <MainWrapperContainer>
        {showButtons && (
          <ScrollButton className="left" onClick={scrollLeft}>
            <BlueLeftArrow />
          </ScrollButton>
        )}
        <MainWrapper ref={scrollContainer}>
          {Object.values(filteredTemplates).map(({ id, label, preview }) => (
            <SingleTemplate key={id} isSelected={templateId === id}>
              <SingleTemplatePreviewWrapper>
                <SingleTemplatePreview
                  isSelected={templateId === id}
                  onClick={() => updateTemplateId(id)}
                >
                  <img src={preview} />
                  <br />
                </SingleTemplatePreview>
              </SingleTemplatePreviewWrapper>
              <TemplateLabel>{label}</TemplateLabel>
            </SingleTemplate>
          ))}
        </MainWrapper>
        {showButtons && (
          <ScrollButton className="right" onClick={scrollRight}>
            <BlueRightArrow />
          </ScrollButton>
        )}
      </MainWrapperContainer>
    );
  }
);

export default withRefluxStore(TemplateSelector, [PreviewStore], ["campaignData"]);
TemplateSelector.displayName = "TemplateSelector";
